import React, { useEffect, useState } from "react";

import { useAuth } from "@/customHooks/useAuth";
import { useSettings } from "@/customHooks/useSettings";
import { useFormik } from "formik";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import { SettingsApiNS as NS } from "~types/api/settings";

import { MemoizedTextField } from "@/components/elements/memoized-textfield/MemoizedTextField";
import { TextFieldWithMaxSymbols } from "@/components/elements/text-field-with-max-symbols/TextFieldWithMaxSymbols";
import Layout from "@/components/layouts/layout/Layout";
import { useContactsValidation } from "@/components/pages/contacts/contactsValidation";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";

interface IInitialValues extends NS.SendContactUsFormArgs {
  agreement: boolean;
}

const initialValues: IInitialValues = {
  name: "",
  email: "",
  subject: "",
  message: "",
  agreement: false
};

const EnSubtitle = () => {
  return (
    <>
      If you have any questions or just want to say hi, please send us an e-mail
      to{" "}
      <Link href={"mailto:hello@cookachu.com"}>
        <a>hello@cookachu.com</a>
      </Link>{" "}
      or use the contact form below.
    </>
  );
};

const NlSubtitle = () => {
  return (
    <>
      Als u vragen heeft of gewoon even hallo wilt zeggen, stuur ons dan een
      e-mail naar{" "}
      <Link href={"mailto:hello@cookachu.com"}>
        <a>hello@cookachu.com</a>
      </Link>{" "}
      of gebruik het contactformulier hieronder.
    </>
  );
};

// let isCurrent = true;

export const ContactsPage = (): JSX.Element => {
  const { t } = useTranslation("contacts");
  const { session, isLoading } = useAuth();
  const validationSchema = useContactsValidation();
  const { sendContactUs } = useSettings();
  const theme = useTheme();
  const router = useRouter();
  const isGreaterThanTablet = useMediaQuery(theme.breakpoints.up("md"));
  const [isCurrent, setIsCurrent] = useState(true);

  const formik = useFormik<IInitialValues>({
    initialValues,
    validationSchema,
    onSubmit: async values => {
      try {
        await sendContactUs.mutateAsync(values);
        toast.success("Your message has been sent");
        formik.resetForm();
      } catch (e) {
        toast.error("Can`t send message. Something went wrong");
      }
    }
  });

  useEffect(() => {
    if (session && isCurrent) {
      const name = `${session.user.first_name} ${session.user.last_name}`;
      const email = session.user.email;
      formik.setFieldValue("name", name);
      formik.setFieldValue("email", email);
      setIsCurrent(false);
    }

    // return () => {
    //   isCurrent = false;
    // };
  }, [session, formik]);

  return (
    <Layout noFooter={!isGreaterThanTablet}>
      <Head>
        <meta name="theme-color" content={theme.palette.primary.main} />
      </Head>
      <Container>
        <Typography
          mt={1.25}
          mb={2.5}
          fontWeight={400}
          sx={{
            fontSize: { xs: 24, md: 48 },
            textAlign: { xs: "center", md: "start" }
          }}>
          {t("title")}
        </Typography>
        <Box sx={{ position: "relative" }}>
          <Image
            alt={"contactus-image"}
            src={"/images/contacts/banner-pc.webp"}
            objectFit="cover"
            layout="responsive"
            width={100}
            height={(9 / 16) * 100}
            quality={100}
            priority
          />
        </Box>
        <Box
          sx={{
            maxWidth: { md: "580px" },
            width: { md: "100%" },
            margin: { md: "0 auto" }
          }}>
          <Typography
            sx={{
              fontSize: { md: 18 },
              my: { xs: 1.25, md: 0 },
              mb: { md: "16px" },
              mt: { md: "40px" }
            }}
            my={1.25}>
            {router.locale === "en" ? <EnSubtitle /> : <NlSubtitle />}
          </Typography>
          <MemoizedTextField
            id={"contact_name_input"}
            data-qa={"contact_name_textfield"}
            disabled={isLoading}
            name={"name"}
            fullWidth
            placeholder={t("name.placeholder")}
            label={t("name.label")}
            error={Boolean(formik.errors.name && formik.touched.name)}
            helperText={formik.errors.name && formik.touched.name}
            value={formik.values.name}
            onChange={formik.handleChange}
            sx={{ mb: 1.25 }}
          />
          <MemoizedTextField
            id={"contact_email_input"}
            data-qa={"contact_email_textfield"}
            disabled={isLoading}
            name={"email"}
            fullWidth
            placeholder={t("email.placeholder")}
            label={t("email.label")}
            error={Boolean(formik.errors.email)}
            helperText={formik.errors.email}
            value={formik.values.email}
            onChange={formik.handleChange}
            sx={{ mb: 1.25 }}
          />
          <MemoizedTextField
            id={"contact_subject_input"}
            data-qa={"contact_subject_textfield"}
            name={"subject"}
            fullWidth
            placeholder={t("subject.placeholder")}
            label={t("subject.label")}
            error={Boolean(formik.errors.subject)}
            helperText={formik.errors.subject}
            value={formik.values.subject}
            onChange={formik.handleChange}
            sx={{ mb: 1.25 }}
          />
          <TextFieldWithMaxSymbols
            maxLength={500}
            multiline={true}
            rows={5}
            id={"contact_message_input"}
            data-qa={"contact_message_textfield"}
            placeholder={t("message.placeholder")}
            label={t("message.label")}
            error={Boolean(formik.errors.message)}
            helperText={formik.errors.message}
            name={"message"}
            fullWidth
            value={formik.values.message}
            onChange={formik.handleChange}
            sx={{ mb: 1.25 }}
          />
          <FormControl
            sx={{ width: "100%" }}
            error={Boolean(formik.errors.agreement)}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  id={"contact_agreement_checkbox"}
                  name={"agreement"}
                  checked={formik.values.agreement}
                  onChange={formik.handleChange}
                  sx={{ p: 0, pr: 1, color: "orange.main", borderWidth: 1 }}
                />
              }
              label={t("policy-agreement")}
            />
            <FormHelperText sx={{ textAlign: "right" }}>
              {formik.errors.agreement}
            </FormHelperText>
          </FormControl>

          <Button
            type={"submit"}
            startIcon={
              sendContactUs.isLoading && <CircularProgress size={20} />
            }
            onClick={() => formik.handleSubmit()}
            disabled={!(formik.isValid && formik.dirty)}
            sx={{ my: 2.5 }}
            color={"green"}
            fullWidth>
            {t("send-button")}
          </Button>
        </Box>
      </Container>
    </Layout>
  );
};
